/**
 * Unified location data layer.
 *
 * Single source of truth for location data consumed by:
 *  - the locations index page (Switzerland map + Europe waitlist)
 *  - the NL `/locations` poster grid
 *  - the city overview page (`/locations/[slug]`)
 *  - the clinic detail page (`/locations/[slug]/[clinicSlug]`)
 *  - the markdown mirrors in `lib/native-markdown/`
 *  - the sitemap and `llms.txt` builders
 *
 * City-level data (name, slug, country, status, displayName, displayOrder,
 * posterImage) lives on the parent `CmsCity` row, reachable via `loc.cityRef`.
 * `cityOf()` is the canonical accessor and throws if the relation isn't
 * populated, so a misconfigured CMS fetch fails loudly instead of silently
 * rendering with undefined data.
 *
 * Per-clinic translation overrides (`cp_desc_*` / `cp_parking_*` /
 * `cp_transport_*` / `cp_langNote_*`) stay until every clinic row holds CMS
 * content for every locale; tracked in a separate cleanup PR.
 */

import type {
  CmsCity,
  CmsClinicHours,
  CmsClinicParkingSection,
  CmsLocation,
  ResolvedClinicService,
} from "@/lib/cms"
import { cmsMediaUrl } from "@/lib/cms"

/**
 * Resolve the parent city for a location. Throws if `cityRef` arrived
 * un-populated (numeric id) or is missing — this means the caller fetched
 * without `?depth=1` + `select[cityRef]=true` and we'd otherwise render
 * with undefined city data.
 */
export function cityOf(loc: CmsLocation): CmsCity {
  const ref = loc.cityRef
  if (ref && typeof ref === "object" && "slug" in ref) return ref
  throw new Error(
    `cityOf: location ${loc.id} has no populated cityRef — fetch with ?depth=1 and select[cityRef]=true`
  )
}

// ─── Locale-specific overrides we still ship in code ──────────

/**
 * Localized display names for cities whose English spelling differs from
 * their German / French / Dutch form. Used until the CMS `displayName`
 * field is populated for every city.
 */
const LOCALIZED_CITY_NAMES: Record<string, Record<string, string>> = {
  de: {
    Geneva: "Genf",
    Vienna: "Wien",
    Munich: "München",
    Brussels: "Brüssel",
    Copenhagen: "Kopenhagen",
  },
  fr: {
    Geneva: "Genève",
    Vienna: "Vienne",
    Brussels: "Bruxelles",
    Copenhagen: "Copenhague",
  },
  nl: { Vienna: "Wenen", Brussels: "Brussel", Copenhagen: "Kopenhagen" },
}

/** Resolve a city's locale-appropriate display name. */
export function resolveCityDisplayName(city: CmsCity, locale: string): string {
  if (city.displayName) return city.displayName
  return LOCALIZED_CITY_NAMES[locale]?.[city.city] || city.city
}

/** Resolve a location's parent-city display name. */
export function resolveDisplayName(loc: CmsLocation, locale: string): string {
  return resolveCityDisplayName(cityOf(loc), locale)
}

function foldName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
}

/**
 * "[Partner] [city]" for a clinic page's meta title, which leads with "Ahead
 * Health … at" so a search result never reads as the partner lab's own page
 * (a customer called us believing we were Z-Point). Most CMS clinic names
 * already carry the city ("Z-Point Zurich", "Unilabs Bern"), so the city is
 * appended only when none of its spellings (CMS, or localized in any
 * language) is in the name, accent-insensitively. Returns null when the name is nothing but the
 * city (DE rows such as "Hamburg"): "Bluttests bei Hamburg" names no partner.
 */
export function resolveClinicPartnerLabel(
  loc: CmsLocation,
  locale: string
): string | null {
  const name = (loc.clinic ?? "").trim()
  if (!name) return null
  const city = cityOf(loc)
  // Any language's spelling counts: the de name "IHR LABOR 1220 Wien" must
  // not become "… Wien Vienna" on the English page.
  const spellings = [
    city.city,
    resolveCityDisplayName(city, locale),
    ...Object.values(LOCALIZED_CITY_NAMES).map((names) => names[city.city]),
  ]
    .filter((s): s is string => Boolean(s))
    .map(foldName)
  const folded = foldName(name)
  if (spellings.includes(folded)) return null
  if (spellings.some((s) => folded.includes(s))) return name
  return `${name} ${resolveCityDisplayName(city, locale)}`
}

// ─── Resolved (page-ready) location shape ─────────────────────

/**
 * Image references resolved to concrete URLs. CMS rows return either an
 * upload object (`{ url, alt }`) when fetched with `depth >= 1` or `null`.
 * The static fallback uses an absolute path under `/public`.
 */
function resolveImageUrl(image: CmsLocation["cardImage"]): string | null {
  if (!image) return null
  if (typeof image === "object" && "url" in image && image.url) {
    return cmsMediaUrl(image.url)
  }
  return null
}

export function resolveCardImage(loc: CmsLocation): string | null {
  return resolveImageUrl(loc.cardImage)
}

export function resolveHeroImage(loc: CmsLocation): string | null {
  return resolveImageUrl(loc.heroImage)
}

export function resolvePosterImage(loc: CmsLocation): string | null {
  return resolveImageUrl(cityOf(loc).posterImage) ?? resolveCardImage(loc)
}

// ─── Translation-key-aware content resolvers ──────────────────

/**
 * Translation lookup contract — pages pass a partial of next-intl's
 * `useTranslations()` so this module stays decoupled from request context.
 * `t.has(key)` lets us prefer the `cp_*` translated override only when it
 * actually exists; otherwise we fall back to whatever the CMS row carries.
 */
export interface LocationsTranslator {
  has: (key: string) => boolean
  (key: string): string
}

function readTranslated(
  t: LocationsTranslator | null | undefined,
  key: string
): string | null {
  if (!t) return null
  const tHas = t.has as (k: string) => boolean
  const tAny = t as unknown as (k: string) => string
  return tHas(key) ? tAny(key) : null
}

export function resolveSlug(loc: CmsLocation): string {
  return cityOf(loc).slug
}

export function resolveClinicSlug(loc: CmsLocation): string | null {
  return loc.clinicSlug ?? null
}

export function resolveDescription(
  loc: CmsLocation,
  t?: LocationsTranslator | null
): string | null {
  if (loc.description) return loc.description
  const cs = resolveClinicSlug(loc)
  if (cs) {
    const translated = readTranslated(t, `cp_desc_${cs}`)
    if (translated) return translated
  }
  return null
}

export function resolveLanguageNote(
  loc: CmsLocation,
  t?: LocationsTranslator | null
): string | null {
  if (loc.languageNote) return loc.languageNote
  const cs = resolveClinicSlug(loc)
  if (cs) {
    const translated = readTranslated(t, `cp_langNote_${cs}`)
    if (translated) return translated
  }
  return null
}

export function resolvePublicTransport(
  loc: CmsLocation,
  t?: LocationsTranslator | null
): string | null {
  if (loc.publicTransport) return loc.publicTransport
  const cs = resolveClinicSlug(loc)
  if (cs) {
    const translated = readTranslated(t, `cp_transport_${cs}`)
    if (translated) return translated
  }
  return null
}

export function resolveHours(loc: CmsLocation): CmsClinicHours[] | null {
  return loc.hours?.length ? loc.hours : null
}

/**
 * Parking shape expected by the clinic detail client. Bullets are plain
 * strings; CMS returns `{ value }[]` because Payload wraps arrays-of-strings
 * inside an object with an `id`. We normalize here so callers stay simple.
 */
export interface ResolvedParkingSection {
  heading?: string | null
  body?: string | null
  bullets?: string[] | null
}

function normalizeParking(
  sections: CmsClinicParkingSection[]
): ResolvedParkingSection[] {
  return sections.map((s) => ({
    heading: s.heading ?? null,
    body: s.body ?? null,
    bullets: s.bullets?.length ? s.bullets.map((b) => b.value) : null,
  }))
}

export function resolveParking(
  loc: CmsLocation,
  t?: LocationsTranslator | null
): ResolvedParkingSection[] | null {
  if (loc.parking?.length) return normalizeParking(loc.parking)
  const cs = resolveClinicSlug(loc)
  if (cs) {
    const translated = readTranslated(t, `cp_parking_${cs}`)
    if (translated) {
      try {
        const parsed = JSON.parse(translated) as ResolvedParkingSection[]
        if (Array.isArray(parsed)) return parsed
      } catch {
        // fall through
      }
    }
  }
  return null
}

/**
 * Project one `imagingServices[]` / `bloodServices[]` row onto the render
 * shape. Reads the `product` relation (AHD-12802 phase 1); the old `service`
 * relation is gone with the `services` collection, and there is deliberately
 * no fallback: a row with no `product` is skipped with a warning.
 */
function renderCmsService(s: {
  product?: { name: string; path?: string | null } | null
  addon?: boolean
}): ResolvedClinicService | null {
  // CMS field is optional. An editor can save a row without picking a product
  // from the catalogue — skip those instead of throwing on `s.product.name`.
  if (!s.product || typeof s.product !== "object" || !s.product.name) {
    console.warn(
      "[locations] skipping clinic service row — missing populated 'product' relation"
    )
    return null
  }
  return {
    name: s.product.name,
    path: s.product.path ?? null,
    addon: !!s.addon,
  }
}

export function resolveImagingServices(
  loc: CmsLocation
): ResolvedClinicService[] {
  return (loc.imagingServices ?? [])
    .map(renderCmsService)
    .filter((s): s is ResolvedClinicService => s !== null)
}

export function resolveBloodServices(
  loc: CmsLocation
): ResolvedClinicService[] {
  return (loc.bloodServices ?? [])
    .map(renderCmsService)
    .filter((s): s is ResolvedClinicService => s !== null)
}

// ─── Filtering helpers ────────────────────────────────────────

export function isActive(loc: CmsLocation): boolean {
  // Per-clinic hiding is now done via Unpublish (the CMS query filters
  // `_status=published`), so `loc.active` no longer exists (AHD-6331). The
  // remaining gate is the city-level waitlist status.
  return cityOf(loc).status === "active"
}

/**
 * Group active clinics by city slug. Used by `/locations/[slug]` and
 * `/locations/[slug]/[clinicSlug]`. Sort: clinic `displayOrder` ascending,
 * with MRI before blood as the tiebreaker so unsorted clinics (all default
 * to 99 in the CMS) keep the historical MRI-first ordering.
 */
export function groupActiveByCitySlug(
  locations: CmsLocation[]
): Map<string, CmsLocation[]> {
  const map = new Map<string, CmsLocation[]>()
  for (const loc of locations) {
    if (!isActive(loc)) continue
    const slug = resolveSlug(loc)
    const group = map.get(slug) || []
    group.push(loc)
    map.set(slug, group)
  }
  for (const group of map.values()) {
    group.sort((a, b) => {
      const ao = a.displayOrder ?? 99
      const bo = b.displayOrder ?? 99
      if (ao !== bo) return ao - bo
      return (a.type === "mri" ? -1 : 1) - (b.type === "mri" ? -1 : 1)
    })
  }
  return map
}

/**
 * Group "coming soon" cities by country code. The CH page renders this as
 * `Country flag → cities` cards in the "Rest of Europe" tab.
 */
export function groupComingSoonByCountry(
  cities: CmsCity[],
  excludeCountry?: string
): { country: string; cities: CmsCity[] }[] {
  const grouped = new Map<string, CmsCity[]>()
  for (const city of cities) {
    if (city.status !== "coming-soon") continue
    if (excludeCountry && city.country === excludeCountry) continue
    const list = grouped.get(city.country) || []
    list.push(city)
    grouped.set(city.country, list)
  }
  for (const list of grouped.values()) {
    list.sort((a, b) => {
      const ao = a.displayOrder ?? 0
      const bo = b.displayOrder ?? 0
      return ao - bo || a.city.localeCompare(b.city)
    })
  }
  return Array.from(grouped.entries()).map(([country, cities]) => ({
    country,
    cities,
  }))
}

export interface ComingSoonSplit {
  /** Unlaunched cities in the page's own market. */
  home: CmsCity[]
  /** Markets that are already live and public — link there, don't waitlist. */
  liveMarkets: { country: string; cities: CmsCity[] }[]
  /** Markets we aren't in yet — the per-city waitlist still applies. */
  waitlist: { country: string; cities: CmsCity[] }[]
}

/**
 * Split coming-soon cities by what the visitor should be offered (AHD-8830).
 *
 * The locations page used to offer a per-city waitlist for every coming-soon
 * city regardless of market state, so German cities kept collecting waitlist
 * signups on the Swiss site after Germany went live. A market that is public
 * gets a link to its own site instead; the page's own market keeps its
 * waitlist (a market can book one city and still be coming soon elsewhere).
 *
 * `isLiveMarket` receives the ISO country code and decides whether that market
 * is publicly live — in the app that's `publicCountries`, so a market converts
 * the day its status flips, with no per-country branch to maintain.
 */
export function splitComingSoonCities(
  cities: CmsCity[],
  homeCountry: string,
  isLiveMarket: (code: string) => boolean
): ComingSoonSplit {
  const home = homeCountry.toUpperCase()
  const groups = groupComingSoonByCountry(cities)
  const foreign = groups.filter((g) => g.country.toUpperCase() !== home)
  return {
    home: groups
      .filter((g) => g.country.toUpperCase() === home)
      .flatMap((g) => g.cities),
    liveMarkets: foreign.filter((g) => isLiveMarket(g.country)),
    waitlist: foreign.filter((g) => !isLiveMarket(g.country)),
  }
}
